import jsPDF from 'jspdf';
import { BasePDFOptions } from '..';
import { PaymentReportItem } from '@/types/pos/payment-reports';
import { capitalizeFirstLetter } from '@/lib/helper';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export async function paymentReportTemplate(
  options: BasePDFOptions<PaymentReportItem[]>
): Promise<void> {
  const {
    data,
    filename = 'payment-report.pdf',
    title = 'Payment Report',
    orientation = 'portrait',
    summary
  } = options;

  const pdf = new jsPDF(orientation === 'landscape' ? 'l' : 'p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  let yPosition = margin;

  // Header
  pdf.setFillColor(30, 41, 59);
  pdf.rect(0, 0, pageWidth, 25, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, margin, 10);

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const formattedTime = currentDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  pdf.text(`Generated: ${formattedDate} at ${formattedTime}`, margin, 17);

  const totalRecordsText = `Total Records: ${summary?.totalCount || data.length}`;
  pdf.text(totalRecordsText, pageWidth - margin, 17, { align: 'right' });

  yPosition = 35;

  // Table header
  pdf.setTextColor(15, 23, 42);
  const headers = ['Payment Method', 'Status', 'Amount'];
  const colWidths = [pageWidth * 0.4, pageWidth * 0.3, pageWidth * 0.2];
  const tableStartX = margin;

  pdf.setFillColor(241, 245, 249);
  pdf.rect(tableStartX, yPosition, pageWidth - (margin * 2), 8, 'F');
  pdf.setDrawColor(203, 213, 225);
  pdf.setLineWidth(0.5);
  pdf.line(tableStartX, yPosition + 8, tableStartX + (pageWidth - (margin * 2)), yPosition + 8);

  pdf.setTextColor(51, 65, 85);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  
  let xPosition = tableStartX;
  headers.forEach((header, index) => {
    const padding = 2;
    if (index === 2) { // Amount right aligned
       pdf.text(header, xPosition + colWidths[index] - padding, yPosition + 5, { align: 'right' });
    } else {
       pdf.text(header, xPosition + padding, yPosition + 5);
    }
    xPosition += colWidths[index];
  });

  yPosition += 8;

  // Rows
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(15, 23, 42);
  const rowHeight = 8;

  data.forEach((row, rowIndex) => {
    if (yPosition > pageHeight - 20) {
      pdf.addPage();
      yPosition = margin;
    }

    if (rowIndex % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(tableStartX, yPosition, pageWidth - (margin * 2), rowHeight, 'F');
    }

    const rowData = [
      capitalizeFirstLetter(row.payment_method),
      capitalizeFirstLetter(row.status),
      formatCurrency(row.total_amount)
    ];

    xPosition = tableStartX;
    rowData.forEach((cellValue, cellIndex) => {
      const padding = 2;
      if (cellIndex === 2) {
        pdf.text(cellValue.toString(), xPosition + colWidths[cellIndex] - padding, yPosition + 5, { align: 'right' });
      } else {
        pdf.text(cellValue.toString(), xPosition + padding, yPosition + 5);
      }
      xPosition += colWidths[cellIndex];
    });

    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.2);
    pdf.line(tableStartX, yPosition + rowHeight, tableStartX + (pageWidth - (margin * 2)), yPosition + rowHeight);
    yPosition += rowHeight;
  });

  pdf.save(filename);
}
